import { createSelector } from '@reduxjs/toolkit'
import { getOnboardingSteps } from '../../utils/getOnboardingSteps'
import { authIsAdminSelector } from '../auth/selectors'
import { RootState } from '../store'

export const providersSelector = (state: RootState) => state.providers.providers

export const providerIdSelector = (state: RootState) =>
  state.providers.providerId

/**
 * The provider the console is currently showing, whole.
 *
 * `providers.find((p) => p.id === providerId)` is written out nine times below
 * this line, once per field anyone has needed so far. A caller that wants more
 * than one field (the support mail wants five) would make it ten, so the
 * lookup itself is the selector and the field selectors read off it.
 */
export const currentProviderSelector = createSelector(
  providersSelector,
  providerIdSelector,
  (providers, providerId) =>
    providers.find((provider) => provider.id === providerId) ?? null,
)

/**
 * What this viewer is, in the one vocabulary the console gates on.
 *
 * `superAdmin` is the console's name for Beast staff, and `auth.isAdmin` is
 * the only way to be it. A provider's own users are `admin`, `fleetManager` or
 * `fleetAssistant`.
 *
 * A provider row claiming `superAdmin` is refused rather than honoured. The
 * GraphQL enum declares the value and `user_roles.role_name` is an
 * unconstrained `text` column, so nothing on the server stops one being
 * written; passing it through here would hand whoever held that row every
 * screen the console keeps for Beast, peer-to-peer included. `null` is the
 * safe reading of a role this console cannot make sense of: it opens nothing.
 */
export const userRoleSelector = createSelector(
  providersSelector,
  providerIdSelector,
  authIsAdminSelector,
  (providers, providerId, isAdmin) => {
    if (isAdmin) {
      return 'superAdmin'
    }

    const provider = providers.find((p) => p.id === providerId)

    if (!provider || provider.roleName === 'superAdmin') {
      return null
    }

    return provider.roleName
  },
)

export const providerCountrySelector = createSelector(
  providersSelector,
  providerIdSelector,
  (providers, providerId) => {
    const provider = providers.find((p) => p.id === providerId)
    return provider ? provider.countryCode : null
  },
)

export const currentProviderMissingSteps = createSelector(
  providersSelector,
  providerIdSelector,
  (providers, providerId) => {
    const provider = providers?.find((p) => p.id === providerId)
    return provider ? provider?.onboardingMissingStep : []
  },
)

export const currentsProviderIncompleteVehicleDataHas = createSelector(
  currentProviderMissingSteps,
  (missingSteps) =>
    missingSteps.some(({ step }) => step === 'incompleteVehicleData'),
)

export const currentsProviderIncompleteVehicleHas = createSelector(
  currentProviderMissingSteps,
  (missingSteps) =>
    missingSteps.some(({ step }) => step === 'incompleteVehicles'),
)

/**
 * No Tesla account the fleet's cars can be imported from: none connected, or
 * the one there was withdrawn. The server decides; see `userProvidersGet`.
 */
export const currentProviderTeslaConnectionMissing = createSelector(
  currentProviderMissingSteps,
  (missingSteps) =>
    missingSteps.some(({ step }) => step === 'incompleteTeslaConnection'),
)

export const currentsProviderIncompleteStripeConnectHas = createSelector(
  currentProviderMissingSteps,
  (missingSteps) =>
    missingSteps.some(({ step }) => step === 'incompleteStripeConnect'),
)

export const currentsProviderIncompleteSubscriptionsHas = createSelector(
  currentProviderMissingSteps,
  (missingSteps) =>
    missingSteps.some(({ step }) => step === 'incompleteSubscriptions'),
)

export const currentsProviderIncompletePricingHas = createSelector(
  currentProviderMissingSteps,
  (missingSteps) =>
    missingSteps.some(({ step }) => step === 'incompletePricing'),
)

export const currentsProviderIncompleteTeamMembersHas = createSelector(
  currentProviderMissingSteps,
  (missingSteps) =>
    missingSteps.some(({ step }) => step === 'incompleteTeamMembers'),
)

export const currentProviderStripeConnectUrl = createSelector(
  currentProviderMissingSteps,
  (missingSteps) =>
    missingSteps.find(({ step }) => step === 'incompleteStripeConnect')?.url,
)

export const currentProviderAddPaymentMethodUrl = createSelector(
  currentProviderMissingSteps,
  (missingSteps) =>
    missingSteps.find(({ step }) => step === 'incompleteProviderPaymentMethod')
      ?.url,
)

export const currentProviderFeatures = createSelector(
  providersSelector,
  providerIdSelector,
  (providers, providerId) => {
    const provider = providers.find((p) => p.id === providerId)
    return provider ? provider?.features : []
  },
)

export const currentProviderFreestyleRentalsEnabled = createSelector(
  currentProviderFeatures,
  (features) =>
    !!features.find(({ name }) => name === 'freestyleEnabled')?.enabled,
)

export const currentProviderUserBalanceEnabled = createSelector(
  currentProviderFeatures,
  (features) =>
    !!features.find(({ name }) => name === 'userBalanceEnabled')?.enabled,
)

export const currentProviderPromoCodeEnabled = createSelector(
  currentProviderFeatures,
  (features) =>
    !!features.find(({ name }) => name === 'promoCodeEnabled')?.enabled,
)

export const currentProviderPreferredBookingEnabled = createSelector(
  currentProviderFeatures,
  (features) =>
    !!features.find(({ name }) => name === 'bookingPreferred')?.enabled,
)

export const currentProviderPreferredSubscriptionEnabled = createSelector(
  currentProviderFeatures,
  (features) =>
    !!features.find(({ name }) => name === 'subscriptionPreferred')?.enabled,
)

export const currentProviderPreferredMode = createSelector(
  currentProviderPreferredBookingEnabled,
  currentProviderPreferredSubscriptionEnabled,
  (bookingPreferred, subscriptionPreferred) =>
    bookingPreferred
      ? 'booking'
      : subscriptionPreferred
        ? 'subscription'
        : null,
)

export const currentProviderOnboardingCompleted = createSelector(
  currentProviderMissingSteps,
  currentProviderPreferredMode,
  (missingSteps, mode) =>
    !missingSteps?.some(({ step }) => {
      const steps = getOnboardingSteps(mode)

      return (
        steps.requiredSteps.includes(step as any) ||
        steps.additionalSteps.includes(step as any)
      )
    }),
)

export const currentProviderUserBanEnabled = createSelector(
  currentProviderFeatures,
  (features) =>
    !!features.find(({ name }) => name === 'userBanEnabled')?.enabled,
)

export const currentProviderUserSwapEnabled = createSelector(
  currentProviderFeatures,
  (features) =>
    !!features.find(({ name }) => name === 'swapRentalUserEnabled')?.enabled,
)

export const currentProviderUserApproveDrivingLicenseEnabled = createSelector(
  currentProviderFeatures,
  (features) =>
    !!features.find(({ name }) => name === 'userApproveDrivingLicenseEnabled')
      ?.enabled,
)

export const currentProviderUserRemoveDrivingLicenseEnabled = createSelector(
  currentProviderFeatures,
  (features) =>
    !!features.find(({ name }) => name === 'userRemoveDrivingLicenseEnabled')
      ?.enabled,
)

export const currentProviderUserTokenEnabled = createSelector(
  currentProviderFeatures,
  (features) =>
    !!features.find(({ name }) => name === 'userTokenEnabled')?.enabled,
)

export const currentProviderSubscription = createSelector(
  providersSelector,
  providerIdSelector,
  (providers, providerId) => {
    const provider = providers.find((p) => p.id === providerId)
    return provider?.subscription
  },
)

/**
 * The shortest booking the selected provider accepts, in hours. Null means they
 * have no minimum of their own and the platform floor applies.
 */
export const currentProviderMinRentalDurationH = createSelector(
  providersSelector,
  providerIdSelector,
  (providers, providerId) =>
    providers.find((provider) => provider.id === providerId)
      ?.minRentalDurationH ?? null,
)

/**
 * How many hours ahead a booking with the selected provider must start. Null
 * means the platform default applies.
 */
export const currentProviderMinBookingLeadTimeH = createSelector(
  providersSelector,
  providerIdSelector,
  (providers, providerId) =>
    providers.find((provider) => provider.id === providerId)
      ?.minBookingLeadTimeH ?? null,
)

/**
 * The selected provider's own free cancellation window, in hours. Null when
 * it has chosen none and Beast's 48 applies.
 */
export const currentProviderFreeCancellationH = createSelector(
  providersSelector,
  providerIdSelector,
  (providers, providerId) =>
    providers.find((provider) => provider.id === providerId)
      ?.freeCancellationH ?? null,
)

const NO_NIGHT_HOURS = { enabled: false, start: null, end: null } as const

/**
 * The hours in which the selected provider accepts no pickup, as stored:
 * switched off or not, so the card can show the window it would switch back on.
 */
export const currentProviderNightHours = createSelector(
  providersSelector,
  providerIdSelector,
  (providers, providerId) => {
    const nightHours = providers.find((provider) => provider.id === providerId)
      ?.nightHours

    return {
      enabled: nightHours?.enabled ?? NO_NIGHT_HOURS.enabled,
      start: nightHours?.start ?? NO_NIGHT_HOURS.start,
      end: nightHours?.end ?? NO_NIGHT_HOURS.end,
    }
  },
)

/**
 * Where operational mail for the selected provider goes, and the address shown
 * to renters in their booking confirmation.
 */
export const currentProviderEmail = createSelector(
  providersSelector,
  providerIdSelector,
  (providers, providerId) =>
    providers.find((provider) => provider.id === providerId)?.email ?? '',
)

/**
 * The selected provider's country, so a screen can name it without a second
 * list of country codes.
 */
export const currentProviderCountryCode = createSelector(
  providersSelector,
  providerIdSelector,
  (providers, providerId) =>
    providers.find((provider) => provider.id === providerId)?.countryCode ??
    null,
)

/**
 * Whether the selected provider holds the exclusive franchise for its country,
 * and where a signup blocked by it is told to write.
 *
 * The address is the server's, not derived here. A screen that rebuilt it from
 * the country would be a second copy of the rule this feature exists to remove.
 */
export const currentProviderCountryExclusivity = createSelector(
  providersSelector,
  providerIdSelector,
  (providers, providerId) =>
    providers.find((provider) => provider.id === providerId)
      ?.countryExclusivity ?? null,
)

/**
 * Which customers the selected fleet's console reaches. `own` until the
 * provider list has loaded, which is also where every fleet starts.
 */
export const currentProviderCustomerAccess = createSelector(
  currentProviderSelector,
  (provider) => provider?.customerAccess ?? 'own',
)

/** Nothing on, which is what a fleet is until Beast staff switch one on. */
const NO_REWARDS = {
  beastBucks: false,
  wheelOfFortune: false,
  inviteCodes: false,
} as const

/**
 * Which renter rewards the fleet on screen runs, as Beast staff set them.
 *
 * All off when the provider is not loaded yet, never a guess: every one of
 * these decides what a renter is charged, and the server reads its own copy.
 */
export const currentProviderRewards = createSelector(
  currentProviderSelector,
  (provider) => provider?.rewards ?? NO_REWARDS,
)

/**
 * Whether this console gives a renter spins by hand: the fleet has the token
 * entitlement and runs its own Wheel of Fortune. A spin is of the fleet's
 * wheel, so there is nothing to give while it does not run.
 */
export const currentProviderSpinsEnabled = createSelector(
  currentProviderUserTokenEnabled,
  currentProviderRewards,
  (tokenEnabled, rewards) => tokenEnabled && rewards.wheelOfFortune,
)
